/** Super Admin role names (bulk approval bypass when role flags are set on role). */
const BULK_APPROVAL_SUPER_ROLE_NAMES = new Set(['super admin', 'superadmin']);

export type PulseRole = {
  name?: string;
  can_approve_products?: boolean | number | string;
  is_super_admin?: boolean | number | string;
  super_admin?: boolean | number | string;
};

export type PulseCurrentUser = {
  role?: PulseRole | null;
};

function isTruthyFlag(value: unknown): boolean {
  if (value === true || value === 1) return true;
  if (typeof value === 'string') {
    const v = value.trim().toLowerCase();
    return v === 'true' || v === '1';
  }
  return false;
}

function normalizeRoleName(name: unknown): string {
  return String(name ?? '').trim().toLowerCase();
}

/** Read session user saved after login (`/users/me` → localStorage). */
export function getCurrentUserFromLocalStorage(): PulseCurrentUser | null {
  try {
    const raw = localStorage.getItem('current_user');
    if (!raw) return null;
    return JSON.parse(raw) as PulseCurrentUser;
  } catch {
    return null;
  }
}

function resolveRole(user: PulseCurrentUser): PulseRole | null {
  if (user?.role && typeof user.role === 'object') return user.role;
  try {
    const roleDetail = localStorage.getItem('role_detail');
    if (roleDetail) return JSON.parse(roleDetail) as PulseRole;
  } catch {
    /* ignore */
  }
  return null;
}

/**
 * Bulk approval UI gate — uses `localStorage.current_user.role` only
 * (`can_approve_products`, `super_admin`, `name`).
 */
export function canAccessBulkApproval(user?: PulseCurrentUser | null): boolean {
  const sessionUser = user ?? getCurrentUserFromLocalStorage();
  if (!sessionUser) return false;

  const role = resolveRole(sessionUser);
  if (!role) return false;

  if (isTruthyFlag(role.can_approve_products)) return true;
  if (isTruthyFlag(role.super_admin)) return true;

  return BULK_APPROVAL_SUPER_ROLE_NAMES.has(normalizeRoleName(role.name));
}

/** Always reads latest `current_user` from localStorage. */
export function canAccessBulkApprovalFromLocalStorage(): boolean {
  return canAccessBulkApproval(getCurrentUserFromLocalStorage());
}

export function isBulkApprovalSuperAdminFromLocalStorage(): boolean {
  const sessionUser = getCurrentUserFromLocalStorage();
  const role = sessionUser?.role;
  if (!role || typeof role !== 'object') {
    try {
      const roleDetail = localStorage.getItem('role_detail');
      if (roleDetail) {
        const parsed = JSON.parse(roleDetail);
        if (isTruthyFlag(parsed?.is_super_admin) || isTruthyFlag(parsed?.super_admin)) return true;
        return BULK_APPROVAL_SUPER_ROLE_NAMES.has(normalizeRoleName(parsed?.name));
      }
    } catch {
      /* ignore */
    }
    return false;
  }
  if (isTruthyFlag(role.is_super_admin) || isTruthyFlag(role.super_admin)) return true;
  return BULK_APPROVAL_SUPER_ROLE_NAMES.has(normalizeRoleName(role.name));
}
